import { useEffect } from "react";
import { ChevronDownIcon } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "zudoku/ui/DropdownMenu.js";
type SlotProps = {
  location: { pathname: string; search: string; hash: string };
  navigate: (to: string) => void;
};

const CRISP_WEBSITE_ID = "2b1495da-3040-495e-8eaa-b3c22e1224fe";

const LOCALES = [
  { id: "en", label: "English", flag: "🇺🇸" },
  { id: "es", label: "Español", flag: "🇪🇸" },
  { id: "pt", label: "Português", flag: "🇧🇷" },
] as const;

type LocaleId = (typeof LOCALES)[number]["id"];

const copy = {
  en: {
    product: "Atomicat",
    website: "Website",
    app: "App",
    help: "Help center",
    docs: "API docs",
    introduction: "Introduction",
    authentication: "Authentication",
    reference: "API reference",
  },
  es: {
    product: "Atomicat",
    website: "Sitio web",
    app: "App",
    help: "Centro de ayuda",
    docs: "Documentación de la API",
    introduction: "Introducción",
    authentication: "Autenticación",
    reference: "Referencia de la API",
  },
  pt: {
    product: "Atomicat",
    website: "Site",
    app: "App",
    help: "Central de ajuda",
    docs: "Documentação da API",
    introduction: "Introdução",
    authentication: "Autenticação",
    reference: "Referência da API",
  },
} as const;

export function currentLocale(pathname: string): LocaleId {
  if (pathname === "/es" || pathname.startsWith("/es/")) return "es";
  if (pathname === "/pt" || pathname.startsWith("/pt/")) return "pt";
  return "en";
}

export function localePath(pathname: string, locale: LocaleId) {
  const bare = pathname.replace(/^\/(es|pt)(?=\/|$)/, "") || "/";
  const path = bare === "/" ? "/introduction" : bare;
  return locale === "en" ? path : `/${locale}${path}`;
}

function Flag({ flag, label }: { flag: string; label: string }) {
  return (
    <span className="text-base leading-none" aria-hidden="true" title={label}>
      {flag}
    </span>
  );
}

export function LanguageMenu({ location, navigate }: SlotProps) {
  const active =
    LOCALES.find((locale) => locale.id === currentLocale(location.pathname)) ??
    LOCALES[0];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="flex h-8 items-center gap-1.5 rounded-full border bg-background px-2.5 text-sm font-medium hover:bg-muted"
        aria-label={active.label}
      >
        <Flag flag={active.flag} label={active.label} />
        <span>{active.label}</span>
        <ChevronDownIcon className="size-3.5 opacity-60" />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {LOCALES.map((locale) => (
          <DropdownMenuItem
            key={locale.id}
            onSelect={() => {
              const next = `${localePath(location.pathname, locale.id)}${location.search}${location.hash}`;
              navigate(next);
            }}
          >
            <Flag flag={locale.flag} label={locale.label} />
            {locale.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const WEBSITE_URL = "https://atomicat.ai";

export function WebsiteLink() {
  return (
    <a
      href={WEBSITE_URL}
      target="_blank"
      rel="noreferrer"
      className="text-sm font-medium text-muted-foreground hover:text-foreground"
    >
      atomicat.ai
    </a>
  );
}

const socialLinks = [
  {
    label: "Instagram",
    href: "https://www.instagram.com/atomicat.br/",
    path: "M12 2.2c3.2 0 3.6 0 4.8.1 3.3.1 4.8 1.7 4.9 4.9.1 1.3.1 1.6.1 4.8s0 3.6-.1 4.8c-.1 3.2-1.7 4.8-4.9 4.9-1.3.1-1.6.1-4.8.1s-3.6 0-4.8-.1c-3.3-.1-4.8-1.7-4.9-4.9-.1-1.3-.1-1.6-.1-4.8s0-3.6.1-4.8c.1-3.2 1.7-4.8 4.9-4.9 1.2-.1 1.6-.1 4.8-.1zM12 0C8.7 0 8.3 0 7.1.1 2.7.3.3 2.7.1 7.1 0 8.3 0 8.7 0 12s0 3.7.1 4.9c.2 4.4 2.6 6.8 7 7 1.2.1 1.6.1 4.9.1s3.7 0 4.9-.1c4.4-.2 6.8-2.6 7-7 .1-1.2.1-1.6.1-4.9s0-3.7-.1-4.9c-.2-4.4-2.6-6.8-7-7C15.7 0 15.3 0 12 0zm0 5.8a6.2 6.2 0 1 0 0 12.4 6.2 6.2 0 0 0 0-12.4zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.4-11.8a1.4 1.4 0 1 0 0 2.8 1.4 1.4 0 0 0 0-2.8z",
  },
  {
    label: "YouTube",
    href: "https://www.youtube.com/@atomicatbr",
    path: "M23.5 6.2a3 3 0 0 0-2.1-2.1C19.5 3.5 12 3.5 12 3.5s-7.5 0-9.4.5a3 3 0 0 0-2.1 2.2C0 8.1 0 12 0 12s0 3.9.5 5.8a3 3 0 0 0 2.1 2.2c1.9.5 9.4.5 9.4.5s7.5 0 9.4-.5a3 3 0 0 0 2.1-2.2c.5-1.9.5-5.8.5-5.8s0-3.9-.5-5.8zM9.5 15.6V8.4L15.8 12l-6.3 3.6z",
  },
  {
    label: "WhatsApp",
    href: "https://wa.me/5551996090665",
    path: "M20.5 3.5A11.8 11.8 0 0 0 12.1 0C5.5 0 .2 5.3.2 11.9c0 2.1.5 4.1 1.6 5.9L0 24l6.3-1.7a11.9 11.9 0 0 0 5.7 1.5h.1c6.6 0 11.9-5.3 11.9-11.9 0-3.2-1.2-6.2-3.5-8.4zM12.1 21.8h-.1a9.9 9.9 0 0 1-5-1.4l-.4-.2-3.7 1 1-3.6-.2-.4a9.9 9.9 0 0 1-1.5-5.3c0-5.5 4.4-9.9 9.9-9.9 2.6 0 5.1 1 7 2.9a9.8 9.8 0 0 1 2.9 7c0 5.5-4.4 9.9-9.9 9.9zm5.4-7.4c-.3-.1-1.8-.9-2-.9-.3-.1-.5-.1-.7.2-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6.1-.1.3-.3.4-.5.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5 0-.1-.7-1.6-.9-2.2-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.1-.3-.2-.6-.3z",
  },
] as const;

export function FooterLinks({ location }: SlotProps) {
  const locale = currentLocale(location.pathname);
  const text = copy[locale];
  const prefix = locale === "en" ? "" : `/${locale}`;
  const columns = [
    {
      title: text.product,
      links: [
        { label: text.website, href: WEBSITE_URL },
        { label: text.app, href: "https://app.atomicat.com.br" },
        { label: text.help, href: "https://help.atomicat.com.br" },
      ],
    },
    {
      title: text.docs,
      links: [
        { label: text.introduction, href: `${prefix}/introduction` },
        { label: text.authentication, href: `${prefix}/authentication` },
        { label: text.reference, href: `${prefix}/api` },
      ],
    },
  ];

  return (
    <div className="flex w-full flex-col gap-12">
      <div className="flex flex-wrap gap-x-24 gap-y-10">
        {columns.map((column) => (
          <div key={column.title} className="min-w-40">
            <span className="text-sm font-semibold">{column.title}</span>
            <ul className="mt-5 space-y-3">
              {column.links.map((link) => {
                const external = /^https?:/.test(link.href);
                return (
                  <li key={link.href}>
                    <a
                      href={link.href}
                      {...(external
                        ? { target: "_blank", rel: "noreferrer" }
                        : {})}
                      className="text-sm text-muted-foreground hover:text-foreground"
                    >
                      {link.label}
                    </a>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </div>
      <div className="flex flex-col gap-4 border-t border-border pt-6 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} Atomicat
        </p>
        <div className="flex items-center gap-2 sm:mr-16">
          {socialLinks.map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noreferrer"
              aria-label={item.label}
              className="flex size-9 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground"
            >
              <svg viewBox="0 0 24 24" className="size-4" aria-hidden="true">
                <path fill="currentColor" d={item.path} />
              </svg>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}

export function CrispChat() {
  useEffect(() => {
    const crispWindow = window as Window & {
      $crisp?: unknown[];
      CRISP_WEBSITE_ID?: string;
    };
    if (crispWindow.CRISP_WEBSITE_ID) return;
    crispWindow.$crisp = [];
    crispWindow.CRISP_WEBSITE_ID = CRISP_WEBSITE_ID;
    crispWindow.$crisp.push(["set", "session:segments", [["docs"]]]);
    const script = document.createElement("script");
    script.src = "https://client.crisp.chat/l.js";
    script.async = true;
    document.head.appendChild(script);
  }, []);

  return null;
}
